// =============================================================================
// VenueHome.js
// /var/www/binko-frontend/src/pages/VenueHome.js
//
// Landing page after venue check-in.
// Two entry points: Browse profiles OR Play the trivia game.
// Reads venue and user from localStorage (set by CheckInForm).
// =============================================================================

import React, { useEffect, useState } from 'react';
import { useNav } from '../App';
import {
  Box, Typography, Button, Card, CardContent,
  Chip, CircularProgress
} from '@mui/material';
import PeopleAltIcon  from '@mui/icons-material/PeopleAlt';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';

import api from '../services/api';
import { C } from '../theme';

export default function VenueHome() {
  const navigate = useNav();
  const [checkin,  setCheckin]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [tournamentBanner, setTournamentBanner] = useState(null);
  const [offers,          setOffers]          = useState([]);
  const [winnerCodes,     setWinnerCodes]     = useState([]);
  const [carouselIdx,     setCarouselIdx]     = useState(0);
  const [walletOpen,      setWalletOpen]      = useState(false);

  useEffect(() => {
    api.get('/checkin/my-checkin')
      .then(r => {
        setCheckin(r.data.checkin);
        const venueId = r.data?.checkin?.venue_id;
        if (venueId) {
          api.get('/venue/offers', { params: { venue_id: venueId } })
            .then(or => setOffers(or.data?.offers || []))
            .catch(() => {});
          api.get('/checkin/my-winner-codes')
            .then(wr => setWinnerCodes(wr.data?.codes || []))
            .catch(() => {});
          api.get('/tournament/upcoming', { params: { venue_id: venueId } })
            .then(tr => {
              const up = tr.data?.upcoming;
              if (up && up.minutes_away <= 15) setTournamentBanner(up);
            })
            .catch(() => {});
        }
      })
      .catch(() => setError('Could not load your check-in.'))
      .finally(() => setLoading(false));
  }, []);

  // Carousel auto-rotate
  useEffect(() => {
    if (offers.length === 0) return;
    const t = setInterval(() => setCarouselIdx(i => (i + 1) % 3), 3400);
    return () => clearInterval(t);
  }, [offers]);

  if (loading) {
    return (
      <Box sx={styles.center}>
        <CircularProgress sx={{ color: C.textPrimary }} />
      </Box>
    );
  }

  if (error || !checkin) {
    return (
      <Box sx={styles.center}>
        <Typography color="error">{error || 'No active check-in found.'}</Typography>
        <Button sx={styles.secondaryBtn} onClick={() => navigate('/home')}>
          Back to Venues
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      {/* Venue header */}
      <Box sx={styles.header}>
        
        <Typography sx={styles.venueName}>{checkin.venue_name}</Typography>
      </Box>

      <Typography sx={styles.greeting}>
        Hey {checkin.alias} 👋
      </Typography>
      <Typography sx={styles.sub}>
        What do you want to do tonight?
      </Typography>

      {/* Tournament banner */}
      {tournamentBanner && (
        <Box sx={styles.tournamentBanner} onClick={() => navigate('/game')}>
          <Typography sx={styles.tournamentBannerText}>
            🏆 Tournament in {tournamentBanner.minutes_away} min — tap Play to join
          </Typography>
        </Box>
      )}


      {/* Offers carousel */}
      {(() => {
        const triviaOffer = offers.find(o => o.offer_type === 'trivia');
        const tournOffer  = offers.find(o => o.offer_type === 'tournament');
        const pendingCode = winnerCodes.find(c => c.status === 'pending');
        const slides = [
          { accent: C.teal,      label: 'Win at Trivia',     text: triviaOffer?.offer_text || "Complete trivia to win tonight's offer" },
          { accent: C.yellow,    label: 'Win the Tournament', text: tournOffer?.offer_text  || 'Win the tournament for a special reward' },
          { accent: C.textMuted, label: 'Terms',              text: 'Offers valid tonight only. Redeem at the bar with your 4-digit code. One redemption per win.' },
        ];
        const slide = slides[carouselIdx];
        return (
          <Box sx={styles.carouselWrap}>
            <Box sx={{ ...styles.carouselSlide, borderColor: slide.accent }}>
              <Typography sx={{ ...styles.carouselLabel, color: slide.accent }}>{slide.label}</Typography>
              <Typography sx={styles.carouselText}>{slide.text}</Typography>
              {pendingCode && carouselIdx < 2 && (
                <Box sx={styles.codePanel}>
                  <Typography sx={styles.codePanelLabel}>Your winner code</Typography>
                  <Typography sx={styles.codePanelCode}>{pendingCode.code}</Typography>
                  <Typography sx={styles.codePanelSub}>Show this to the bartender</Typography>
                </Box>
              )}
            </Box>
            <Box sx={styles.carouselDots}>
              {slides.map((_, i) => (
                <Box key={i} onClick={() => setCarouselIdx(i)}
                  sx={{ ...styles.dot, background: i === carouselIdx ? slide.accent : C.border }} />
              ))}
            </Box>
          </Box>
        );
      })()}

      {/* Wins wallet */}
      {winnerCodes.filter(c => c.status !== 'expired').length > 0 && (
        <Box sx={styles.walletWrap}>
          <Box sx={styles.walletHeader} onClick={() => setWalletOpen(o => !o)}>
            <Typography sx={styles.walletTitle}>My Wins Tonight</Typography>
            <Typography sx={styles.walletToggle}>{walletOpen ? '▲' : '▼'}</Typography>
          </Box>
          {walletOpen && winnerCodes.filter(c => c.status !== 'expired').map(wc => (
            <Box key={wc.id} sx={styles.walletRow}>
              <Box sx={styles.walletLeft}>
                <Typography sx={styles.walletGame}>{wc.game_type === 'trivia' ? 'Trivia' : 'Tournament'}</Typography>
                <Typography sx={styles.walletOffer}>{wc.offer_text || 'Winner offer'}</Typography>
                <Typography sx={styles.walletTime}>{new Date(wc.won_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
              </Box>
              <Box sx={styles.walletRight}>
                <Typography sx={styles.walletCode}>{wc.code}</Typography>
                <Box sx={{ ...styles.walletBadge, background: wc.status === 'redeemed' ? C.textDisabled : C.teal }}>
                  <Typography sx={styles.walletBadgeText}>{wc.status === 'redeemed' ? 'Redeemed' : 'Pending'}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Mode cards */}
      <Box sx={styles.cardRow}>

        {/* Browse */}
        <Card sx={styles.modeCard} onClick={() => navigate('/inhere')}>
          <CardContent sx={styles.modeCardContent}>
            <PeopleAltIcon sx={{ fontSize: 48, color: C.teal, mb: 1.5 }} />
            <Typography sx={styles.modeTitle}>Who's Here?</Typography>
            <Typography sx={styles.modeDesc}>
              See who's around tonight and send a Hi
            </Typography>
            <Chip
              label={`${(checkin.total_likes - checkin.likes_used)} Hi${(checkin.total_likes - checkin.likes_used) !== 1 ? 's' : ''} left`}
              size="small"
              sx={styles.chip}
            />
          </CardContent>
        </Card>

        {/* Play Game */}
        <Card sx={styles.modeCard} onClick={() => navigate('/game')}>
          <CardContent sx={styles.modeCardContent}>
            <SportsEsportsIcon sx={{ fontSize: 48, color: C.yellow, mb: 1.5 }} />
            <Typography sx={styles.modeTitle}>Play with the Room</Typography>
            <Typography sx={styles.modeDesc}>
              Live trivia with everyone here. Meet people between rounds.
            </Typography>
            <Chip label="Live 🎮" size="small" sx={{ ...styles.chip, background: `${C.yellow}22`, color: C.yellow }} />
          </CardContent>
        </Card>

      </Box>

      {/* Bottom nav */}
      <Box sx={styles.bottomRow}>
        <Button sx={styles.textBtn} onClick={() => navigate('/chats')}>
          💬 My Chats
        </Button>
        <Button sx={styles.textBtn} onClick={() => navigate('/home')}>
          Change Venue
        </Button>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
  root: {
    minHeight: '100vh',
    background: C.bgApp,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    px: 2,
    pt: 4,
    pb: 4
  },
  center: {
    minHeight: '100vh',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: C.bgApp
  },
  header: {
    display: 'flex', alignItems: 'center', mb: 2
  },
  venueName: {
    color: C.teal, fontWeight: 600, fontSize: '0.95rem', letterSpacing: 1
  },
  greeting: {
    color: C.textPrimary, fontSize: '1.6rem', fontWeight: 700, mb: 0.5, textAlign: 'center'
  },
  sub: {
    color: C.textMuted, fontSize: '0.95rem', mb: 4, textAlign: 'center'
  },
  cardRow: {
    display: 'flex', gap: 2, width: '100%', maxWidth: 420,
    flexDirection: 'column'
  },
  modeCard: {
    background: C.bgCard,
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    cursor: 'pointer',
    transition: 'transform 0.15s, border-color 0.15s',
    '&:hover': {
      transform: 'translateY(-2px)',
      borderColor: C.teal
    }
  },
  modeCardContent: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', textAlign: 'center',
    py: 3
  },
  modeTitle: {
    color: C.textPrimary, fontWeight: 700, fontSize: '1.15rem', mb: 0.5
  },
  modeDesc: {
    color: C.textMuted, fontSize: '0.85rem', mb: 1.5, lineHeight: 1.5
  },
  chip: {
    background: `${C.teal}22`, color: C.teal,
    fontSize: '0.75rem', fontWeight: 600
  },
  bottomRow: {
    display: 'flex', gap: 2, mt: 4
  },
  textBtn: {
    color: C.textDisabled, fontSize: '0.8rem',
    '&:hover': { color: C.textPrimary }
  },
  tournamentBanner: {
    width: '100%', maxWidth: 420,
    background: 'rgba(251,188,21,0.12)',
    border: `1px solid rgba(251,188,21,0.35)`,
    borderRadius: 2, px: 2, py: 1.2, mb: 2,
    cursor: 'pointer',
    '&:hover': { background: 'rgba(251,188,21,0.2)' }
  },
  tournamentBannerText: {
    color: C.yellow, fontWeight: 600, fontSize: '0.88rem', textAlign: 'center'
  },
  secondaryBtn: {
    mt: 2, color: C.teal, border: `1px solid ${C.teal}`,
    '&:hover': { background: `${C.teal}22` }
  },
  carouselWrap: {
    width: '100%', maxWidth: 420, mb: 2,
  },
  carouselSlide: {
    background: C.bgCard,
    border: '1px solid',
    borderRadius: 3,
    px: 2.5, py: 2,
    minHeight: 90,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    textAlign: 'center', transition: 'border-color 0.4s',
  },
  carouselLabel: {
    fontWeight: 700, fontSize: '0.78rem', letterSpacing: 1,
    textTransform: 'uppercase', mb: 0.75,
  },
  carouselText: {
    color: C.textMuted, fontSize: '0.88rem', lineHeight: 1.5,
  },
  codePanel: {
    mt: 1.5, px: 2, py: 1,
    background: 'rgba(13,157,165,0.1)',
    border: '1px solid rgba(13,157,165,0.3)',
    borderRadius: 2, textAlign: 'center',
  },
  codePanelLabel: {
    color: C.textMuted, fontSize: '0.72rem', letterSpacing: 0.5, mb: 0.25,
  },
  codePanelCode: {
    color: C.teal, fontWeight: 800, fontSize: '2rem', letterSpacing: 6, lineHeight: 1.2,
  },
  codePanelSub: {
    color: C.textDisabled, fontSize: '0.7rem', mt: 0.25,
  },
  carouselDots: {
    display: 'flex', justifyContent: 'center', gap: 0.75, mt: 1,
  },
  dot: {
    width: 7, height: 7, borderRadius: '50%', cursor: 'pointer',
    transition: 'background 0.3s',
  },
  walletWrap: {
    width: '100%', maxWidth: 420, mb: 2,
    background: C.bgCard,
    border: `1px solid ${C.border}`,
    borderRadius: 3, overflow: 'hidden',
  },
  walletHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    px: 2, py: 1.5, cursor: 'pointer',
    '&:hover': { background: C.bgElevated },
  },
  walletTitle: {
    color: C.textPrimary, fontWeight: 600, fontSize: '0.88rem',
  },
  walletToggle: {
    color: C.textMuted, fontSize: '0.75rem',
  },
  walletRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    px: 2, py: 1.25,
    borderTop: `1px solid ${C.border}`,
  },
  walletLeft: {
    display: 'flex', flexDirection: 'column', gap: 0.25,
  },
  walletGame: {
    color: C.teal, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5,
  },
  walletOffer: {
    color: C.textPrimary, fontSize: '0.85rem',
  },
  walletTime: {
    color: C.textMuted, fontSize: '0.72rem',
  },
  walletRight: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5,
  },
  walletCode: {
    color: C.textPrimary, fontWeight: 800, fontSize: '1.4rem', letterSpacing: 4,
  },
  walletBadge: {
    borderRadius: 1, px: 1, py: 0.25,
  },
  walletBadgeText: {
    color: '#fff', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
  },

};
